/**
 * Bookmark Store — line-level bookmarks scoped to the current workspace.
 *
 * Bookmarks are persisted in `.mdspace` and broadcast via Engine
 * `event/workspace.bookmarksChanged`. This store mirrors the authoritative
 * Engine state and exposes thin RPC wrappers for mutations.
 */

import type { Bookmark } from "@magic-md/engine-client/protocol";
import { create } from "zustand";

import { getPlatformService } from "../platform";

export type { Bookmark };

interface BookmarkState {
    bookmarks: Bookmark[];

    /** Set the entire bookmark list (called on workspace load and via event). */
    _setBookmarks(items: Bookmark[]): void;

    /** Refresh from Engine (best-effort; safe to call on init). */
    refresh(): Promise<void>;

    /** Add a new bookmark at path:line; returns the created/existing bookmark. */
    add(input: {
        path: string;
        line: number;
        column?: number;
        label?: string;
        note?: string;
    }): Promise<Bookmark | null>;

    /** Remove a bookmark by id. */
    remove(id: string): Promise<boolean>;

    /** Toggle a bookmark at path:line. Returns true if added, false if removed. */
    toggle(path: string, line: number): Promise<boolean>;

    /** Update a bookmark's metadata. */
    update(
        id: string,
        patch: { label?: string | null; note?: string | null; line?: number },
    ): Promise<Bookmark | null>;

    /** Get bookmarks for a specific file path. */
    getForPath(path: string): Bookmark[];

    /** Find a bookmark at the given path:line, if any. */
    findAt(path: string, line: number): Bookmark | null;
}

export const useBookmarkStore = create<BookmarkState>((set, get) => ({
    bookmarks: [],

    _setBookmarks(items) {
        set({ bookmarks: [...items] });
    },

    async refresh() {
        try {
            const result = await getPlatformService().engine.workspaceBookmarkList();
            set({ bookmarks: [...result.items] });
        } catch {
            // Engine offline / no workspace; keep local state.
        }
    },

    async add(input) {
        try {
            const result = await getPlatformService().engine.workspaceBookmarkAdd(input);
            if (result.success) {
                // Optimistic merge; authoritative update will arrive via event.
                const next = [...get().bookmarks];
                const idx = next.findIndex((b) => b.id === result.item.id);
                if (idx >= 0) next[idx] = result.item;
                else next.push(result.item);
                set({ bookmarks: next });
                return result.item;
            }
            return null;
        } catch {
            return null;
        }
    },

    async remove(id) {
        try {
            const result = await getPlatformService().engine.workspaceBookmarkRemove({ id });
            if (result.success) {
                set({ bookmarks: get().bookmarks.filter((b) => b.id !== id) });
            }
            return result.success;
        } catch {
            return false;
        }
    },

    async toggle(path, line) {
        const existing = get().findAt(path, line);
        if (existing) {
            await get().remove(existing.id);
            return false;
        }
        await get().add({ path, line });
        return true;
    },

    async update(id, patch) {
        try {
            const result = await getPlatformService().engine.workspaceBookmarkUpdate({
                id,
                ...patch,
            });
            if (result.success && result.item) {
                set({
                    bookmarks: get().bookmarks.map((b) => (b.id === id ? result.item! : b)),
                });
                return result.item;
            }
            return null;
        } catch {
            return null;
        }
    },

    getForPath(path) {
        return get().bookmarks.filter((b) => b.path === path);
    },

    findAt(path, line) {
        return get().bookmarks.find((b) => b.path === path && b.line === line) ?? null;
    },
}));
